import { StatusColors } from '@euler/components/Colors';
import Euler, { EulerViewProps } from '@euler/components/layout';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { OrderFieldVisibility } from '@euler/model';
import { Maybe } from '@euler/types';
import { getVehicleAgeByVin } from '@euler/utils/vin/getVehicleAgeByVin';
import { memo } from 'react';
import { OrderSummaryInfoField } from './SummaryInfoField';
import { OrderSummaryInlineInfoCell } from './SummaryInlineInfoCell';

export const InlineOrderInfoCells = memo(
  ({
    licensePlateNo,
    vin,
    vehicleMileage,
    vehicleFuelType,
    serviceRecordCount,
    customerSigned,
    licensePlateNoVisibility,
    vehicleMileageVisibility,
    vehicleFuelTypeVisibility,
    serviceRecordCountVisibility,
    customerSignedVisibility,
    hideServiceRecordCount,
    onServiceRecordsPress,
    ...props
  }: {
    customerSigned?: boolean;
    licensePlateNo: string;
    vin: string;
    vehicleMileage: number;
    vehicleFuelType?: Maybe<string>;
    serviceRecordCount?: Maybe<number>;
    licensePlateNoVisibility?: OrderFieldVisibility;
    vehicleMileageVisibility?: OrderFieldVisibility;
    vehicleFuelTypeVisibility?: OrderFieldVisibility;
    serviceRecordCountVisibility?: OrderFieldVisibility;
    customerSignedVisibility?: OrderFieldVisibility;
    hideServiceRecordCount?: boolean;
    onServiceRecordsPress?: () => void;
  } & EulerViewProps) => {
    const { rpx } = useResponsiveContext();
    const age = getVehicleAgeByVin(vin);
    return (
      <Euler.View
        flexDirection="row"
        justifyContent="space-around"
        alignItems="center"
        paddingHorizontal={rpx(0)}
        {...props}
      >
        <OrderSummaryInfoField
          value={vehicleMileage}
          valid={x => x > 0}
          visibility={vehicleMileageVisibility}
        >
          <OrderSummaryInlineInfoCell
            label="进店里程"
            content={vehicleMileage > 0 ? `${vehicleMileage}公里` : '未知'}
          />
        </OrderSummaryInfoField>

        <OrderSummaryInfoField
          value={licensePlateNo}
          visibility={licensePlateNoVisibility}
        >
          <OrderSummaryInlineInfoCell
            label="车龄"
            content={age > 0 ? `${age}年` : '未知'}
          />
        </OrderSummaryInfoField>

        <OrderSummaryInfoField
          value={vehicleFuelType}
          visibility={vehicleFuelTypeVisibility}
          defaultVisible
        >
          <OrderSummaryInlineInfoCell
            label="燃油类型"
            content={vehicleFuelType ?? '未知'}
          />
        </OrderSummaryInfoField>

        {!hideServiceRecordCount && (
          <OrderSummaryInfoField
            value={serviceRecordCount ?? 0}
            visibility={serviceRecordCountVisibility}
          >
            <OrderSummaryInlineInfoCell
              label="服务记录"
              content={
                serviceRecordCount != null && serviceRecordCount > 0
                  ? String(serviceRecordCount)
                  : '(无)'
              }
              contentStyle={!serviceRecordCount ? { color: 'gray' } : undefined}
              onPress={onServiceRecordsPress}
            />
          </OrderSummaryInfoField>
        )}

        <OrderSummaryInfoField
          value={customerSigned}
          visibility={customerSignedVisibility}
        >
          <OrderSummaryInlineInfoCell
            label="客户签字"
            content={customerSigned ? '已签字' : '未签字'}
            contentStyle={{
              color: customerSigned
                ? StatusColors.Success
                : StatusColors.Danger,
            }}
          />
        </OrderSummaryInfoField>
      </Euler.View>
    );
  },
);
